import React, { useEffect, useState } from "react";
import { CloseIcon } from "asset/icons";
import Loader from "component/loader";
import Input from "component/form/Input";
import axios from "axios";
import { successAlert, errorAlert } from "utilities/alerts";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../firebase/firebase";
import useScreenSize from "hooks/useScreenSize";

const RatingModal = ({
    isOpen,
    onClose,
    onSuccess,
    reviewType,
    title = "How do you like your experience?",
    subtitle = "We’d like to know how satisfied you are",
    ratingLabels = ["I don't like it", "It's great"],
    showFeedback = true,
    showFutureFeedbackToggle = true,
    firebaseEventName = "user_feedback_rating",
    apiEndpoint = "/v1/reviews",
    userId,
    featureVersion = "1",
}) => {
    const [rate, setRate] = useState(0);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [receiveMoreFeedback, setReceiveMoreFeedback] = useState(false);
    const { screenWidth } = useScreenSize();

    useEffect(() => {
        if (screenWidth > 650) {
            document.body.style.overflowY = "hidden";
            return () => {
                document.body.style.overflowY = "auto";
            };
        }
    }, [screenWidth]);

    const sendRating = async () => {
        try {
            setLoading(true);

            await axios.post(apiEndpoint, {
                rating: String(rate),
                reason,
                reviewType: reviewType,
                isFeedback: receiveMoreFeedback,
                userId,
            });

            logEvent(analytics, firebaseEventName, {
                rating: rate,
                feedback_reason: reason || "No feedback provided",
                userId,
            });

            successAlert("Feedback Sent. Thank you!");
            onSuccess?.();
            onClose();
        } catch (error) {
            errorAlert(error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed z-[2000] w-screen h-screen left-0 top-0 bg-[#1D29394D]" />
            <div className="bg-white rounded-2xl !pt-[30px] !pb-4 fixed max-sm:w-[calc(100%-32px)] right-4 sm:right-5 bottom-4 sm:bottom-5 z-[2100] shadow-[0px_8px_5px_0px_#00000014,0px_20px_13px_0px_#00000008]">
                <div className="flex !px-6 justify-between">
                    <h3 className="font-semibold text-[22px] text-[#1F2937] !mr-3 580:!mr-12">
                        {title}
                        <span className="block !mt-[7px] font-normal text-sm text-[#6B7280]">
                            {subtitle}
                        </span>
                    </h3>
                    <CloseIcon
                        className="size-10 375:size-8 475:size-6 *:stroke-main-grey -translate-y-1 cursor-pointer rounded-full"
                        onClick={onClose}
                    />
                </div>

                <div className="!px-6 grid grid-cols-5 max-400:text-sm !gap-1 400:!gap-2 !mt-[22px]">
                    {[1, 2, 3, 4, 5].map((value) => (
                        <div
                            key={value}
                            onClick={() => setRate(value)}
                            className={`bg-page-bg sm:w-[95px] h-[46px] 400:h-[56px] rounded-lg  hover:bg-[#EBE4FF] !border cursor-pointer grid place-items-center transition-all duration-200 ${
                                rate === value
                                    ? "!border-[#A173FF] !bg-[#EBE4FF]"
                                    : "!border-transparent"
                            }`}
                        >
                            {value}
                        </div>
                    ))}
                </div>

                <p className="flex items-center justify-between px-6 mt-2.5 font-medium text-sm text-main-grey">
                    {ratingLabels[0]}
                    <span>{ratingLabels[1]}</span>
                </p>

                {showFeedback && rate > 0 && (
                    <div className="px-6 pt-7">
                        <p className="text-[#475467] font-medium text-sm mb-1">
                            Tell us how you really feel
                        </p>
                        <textarea
                            className="rounded-lg resize-none h-[96px] w-full p-4 shadow-inner bg-[#F9FAFB]"
                            placeholder="Describe your experience..."
                            onChange={(evt) => setReason(evt.target.value)}
                        />
                        {showFutureFeedbackToggle && (
                            <div className="flex justify-between items-center font-[450] !mt-11 text-main !mb-7 gap-8 max-400:text-sm">
                                I’d like to give more feedback in the future
                                <Toggle
                                    toggle={receiveMoreFeedback}
                                    setToggle={setReceiveMoreFeedback}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end !px-6 !pt-4 pb-3 border-t mt-8 border-[#E7E9FB]">
                    <button
                        onClick={sendRating}
                        disabled={loading || rate === 0}
                        className={`${
                            loading ? "!py-2.5 !px-[41px] pointer-events-none" : "!py-2 !px-6"
                        } rounded-lg font-[450] text-[#FBFAFF] ${
                            rate && reason ? "!bg-secondary" : "!bg-[#D9CDFF] pointer-events-none"
                        }`}
                    >
                        {loading ? <Loader /> : "Submit"}
                    </button>
                </div>
            </div>
        </>
    );
};

const Toggle = ({ toggle, setToggle }) => (
    <div
        onClick={() => setToggle((prev) => !prev)}
        className={`w-[58px] 375:w-[50px] 420:w-[42.03px] h-[22px] rounded-[33.33px] cursor-pointer transition-colors ease-in-out duration-200 ${
            toggle ? "!bg-secondary" : "bg-[#D9DBE9]"
        }`}
    >
        <div
            className={`size-6 rounded-full border transition-all ease-in-out duration-200 !border-[#E7E9FB] bg-[#F6F8FA] ${
                toggle ? "translate-x-5" : "-translate-x-[1px]"
            } -translate-y-[1px] !shadow-[0px_3.33px_5px_-3.33px_rgba(16,24,40,0.1),0px_0px_16.67px_-2.5px_rgba(16,24,40,0.38)]`}
        />
    </div>
);

export default RatingModal;
